import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Drawer,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import { Theme } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import AppTablePagenation from "../../../../../../views/components/common/pages/AppTablePagenation";
import AppTableHeaderCell from "../../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../../views/components/common/pages/AppTableRowCell";
import { SortType } from "../../../../../../models/SortType";
import {
  DailyTasksTatDistribution,
  DailyTasksTatDistributionBucket,
  DailyTasksTatStat,
  DailyTasksTatTableColumn,
  DAILY_TASKS_ACTIVITY_DRAWER_COPY,
  isDailyTasksActivityMetric,
} from "../../../../../../models/Dashboard/DailyTasks/DailyTasksTat";
import {
  DailyTasksTatRecordScope,
  DailyTasksTatSortDir,
} from "../../../../../../models/Dashboard/DailyTasks/DailyTasksTat";
import { OpsTatStatus } from "../../../../../../models/Dashboard/OpsTat/OpsTatFilters";
import { DailyTasksStore } from "../../../../stores/dashboards/DailyTasksStore";
import { trackDailyTasksAction } from "../../../../../../analytics/dailyTasksTracking";
import DailyTasksDrawer from "./DailyTasksDrawer";
import {
  formatOpsTatStatValue,
  getOpsTatStatusColors,
} from "../ops-tat/opsTatFormat";
import { dailyTasksChipSx, DailyTasksChipTone } from "./dailyTasksChips";
import {
  getModernPageColors,
  modernDrawerBodySx,
  modernDrawerHeaderSx,
  modernTableBodyCellSx,
  modernTableHeaderCellSx,
  modernTablePaperSx,
  modernTableRowSx,
} from "../../../../../../styles/modernListingPageSx";

const DRAWER_Z_OFFSET = 2000;
const MAX_PAGE_SIZE = 100;
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const SCOPE_OPTIONS: Array<{ id: DailyTasksTatRecordScope; label: string }> = [
  { id: "measured", label: "Measured" },
  { id: "open", label: "Open" },
  { id: "all", label: "All" },
];

const STAGE_SORT = [
  "tat_days",
  "over",
  "start_date",
  "end_date",
  "order_id",
  "product_id",
];
const CYCLE_SORT = ["tat_days", "over", "start_date", "end_date", "order_id"];

const ACTIVITY_SORT = [
  "event_date",
  "order_id",
  "product_id",
  "client",
  "order_type",
  "actor",
  "status",
];

const SORT_WHITELIST: Record<string, string[]> = {
  created_to_logo: STAGE_SORT,
  created_to_upload: STAGE_SORT,
  created_to_last_upload: STAGE_SORT,
  upload_to_approval: STAGE_SORT,
  top_drag: STAGE_SORT,
  cycle_total: CYCLE_SORT,
  sla_adherence: CYCLE_SORT,
  custom_mix: ["start_date", "order_id", "path", "product_count"],
  activity_logos: [...ACTIVITY_SORT, "scope"],
  activity_mockups: ACTIVITY_SORT,
  activity_products: [
    "event_date",
    "order_id",
    "product_id",
    "client",
    "order_type",
    "files",
    "status",
  ],
  activity_rework: ACTIVITY_SORT,
  activity_rejected: ACTIVITY_SORT,
  activity_approved: ACTIVITY_SORT,
  finance_to_po: STAGE_SORT,
  po_to_vendor: [...STAGE_SORT, "po_id"],
  po_to_raw_in: STAGE_SORT,
  raw_in_to_raw_qc: STAGE_SORT,
  activity_raw_grns: ACTIVITY_SORT,
  activity_raw_full: ACTIVITY_SORT,
  activity_raw_qc: ACTIVITY_SORT,
  activity_raw_rejects: ACTIVITY_SORT,
  raw_qc_to_print_out: STAGE_SORT,
  activity_print_sends: ACTIVITY_SORT,
  activity_print_full: ACTIVITY_SORT,
  activity_print_qty: ACTIVITY_SORT,
  print_out_to_print_in: STAGE_SORT,
  print_in_to_print_qc: STAGE_SORT,
  activity_print_inwards: ACTIVITY_SORT,
  activity_print_in_full: ACTIVITY_SORT,
  activity_print_in_qc: ACTIVITY_SORT,
  activity_print_in_rejects: ACTIVITY_SORT,
  request_to_project: STAGE_SORT,
  request_to_finance: STAGE_SORT,
  created_to_variants: STAGE_SORT,
  created_to_invoice: STAGE_SORT,
  activity_project_requests: ACTIVITY_SORT,
  activity_project_finance: ACTIVITY_SORT,
  activity_project_variants: ACTIVITY_SORT,
  activity_project_invoices: ACTIVITY_SORT,
};

interface StatusTone {
  fg: string;
  bg: string;
}

interface DailyTasksDrilldownDrawerProps {
  store: DailyTasksStore;
}

const getSortWhitelist = (
  metricId: string,
  _columns: DailyTasksTatTableColumn[],
): string[] => {
  try {
    return SORT_WHITELIST[metricId] ?? [];
  } catch (error) {
    console.error(error);
    return [];
  }
};

const formatStatValue = (stat: DailyTasksTatStat): string => {
  try {
    return formatOpsTatStatValue(stat.value, stat.unit ?? "", stat.label);
  } catch (error) {
    console.error(error);
    return "—";
  }
};

const getSerialNumber = (
  page: number,
  pageSize: number,
  index: number,
): number => {
  try {
    return (Math.max(page, 1) - 1) * Math.max(pageSize, 1) + index + 1;
  } catch (error) {
    console.error(error);
    return index + 1;
  }
};

const getVisibleRowRange = (
  page: number,
  pageSize: number,
  total: number,
  rowCount: number,
): { from: number; to: number } => {
  try {
    if (total <= 0 || rowCount <= 0) {
      return { from: 0, to: 0 };
    }
    const from = (Math.max(page, 1) - 1) * Math.max(pageSize, 1) + 1;
    return { from, to: from + rowCount - 1 };
  } catch (error) {
    console.error(error);
    return { from: 0, to: 0 };
  }
};

const getCyclesCount = (stats: DailyTasksTatStat[]): number | null => {
  try {
    const cycles = stats.find(
      (stat) => stat.label.trim().toLowerCase() === "cycles",
    );
    if (cycles?.value == null || !Number.isFinite(cycles.value)) {
      return null;
    }
    return cycles.value;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const formatUnknownText = (value: unknown): string => {
  try {
    if (value == null || value === "") {
      return "—";
    }
    return String(value);
  } catch (error) {
    console.error(error);
    return "—";
  }
};

const formatNumberCell = (value: unknown, unit?: string): string => {
  try {
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return "—";
    }
    return `${value}${unit ?? ""}`;
  } catch (error) {
    console.error(error);
    return "—";
  }
};

const getStatusTone = (
  theme: Theme,
  status: OpsTatStatus | "clay",
): StatusTone => getOpsTatStatusColors(theme, status);

const columnAlign = (column: DailyTasksTatTableColumn): "left" | "right" =>
  column.type === "number" ? "right" : "left";

const columnMinWidth = (column: DailyTasksTatTableColumn): string => {
  if (column.type === "date" || column.type === "datetime") return "168px";
  if (column.type === "number") return "88px";
  if (column.type === "pill") return "96px";
  return "120px";
};

const tableHeaderSx = (theme: Theme, align: "left" | "right") => {
  const colors = getModernPageColors(theme);
  return {
    ...modernTableHeaderCellSx(theme),
    textAlign: align,
    whiteSpace: "nowrap" as const,
    fontSize: "0.75rem",
    fontWeight: 700,
    verticalAlign: "middle" as const,
    "& .MuiTableSortLabel-root": {
      fontSize: "0.75rem",
      fontWeight: 700,
      color: colors.title,
      width: "100%",
      justifyContent: align === "right" ? "flex-end" : "flex-start",
      "&.Mui-active": {
        color: colors.title,
      },
    },
  };
};

const tableBodySx = (theme: Theme, align: "left" | "right") => ({
  ...modernTableBodyCellSx(theme),
  textAlign: align,
  verticalAlign: "middle" as const,
  fontSize: "0.75rem",
});

const formatBucketLabel = (bucket: DailyTasksTatDistributionBucket): string => {
  try {
    if (bucket.hi == null) {
      return `${bucket.lo}+`;
    }
    return `${bucket.lo}–${bucket.hi}`;
  } catch (error) {
    console.error(error);
    return "—";
  }
};

const getHistogramMarkerX = (
  value: number | null,
  buckets: DailyTasksTatDistributionBucket[],
  barX: (index: number) => number,
  barWidth: number,
): number | null => {
  try {
    if (value == null || !Number.isFinite(value) || buckets.length === 0) {
      return null;
    }

    for (let index = 0; index < buckets.length; index += 1) {
      const { lo, hi } = buckets[index];
      const start = barX(index);
      if (hi == null) {
        if (value >= lo) {
          return start + barWidth * 0.2;
        }
        continue;
      }
      if (value >= lo && value < hi) {
        const span = hi - lo || 1;
        return start + ((value - lo) / span) * barWidth;
      }
      if (index === buckets.length - 1 && value === hi) {
        return start + barWidth;
      }
    }

    if (value < buckets[0].lo) {
      return barX(0);
    }
    const lastIndex = buckets.length - 1;
    return barX(lastIndex) + barWidth;
  } catch (error) {
    console.error(error);
    return null;
  }
};

const DailyTasksHistogram: React.FC<{
  distribution: DailyTasksTatDistribution;
}> = ({ distribution }) => {
  const theme = useTheme();
  const customColors = theme.palette.customColors;
  const { buckets, p50, p90 } = distribution;

  if (!buckets.length) {
    return null;
  }

  const width = 720;
  const height = 148;
  const pad = { top: 18, right: 10, bottom: 28, left: 10 };
  const innerWidth = width - pad.left - pad.right;
  const innerHeight = height - pad.top - pad.bottom;
  const gap = 4;
  const barWidth = Math.max(
    (innerWidth - gap * Math.max(buckets.length - 1, 0)) / buckets.length,
    2,
  );
  const maxCount = Math.max(...buckets.map((bucket) => bucket.count), 1);
  const barX = (index: number) => pad.left + index * (barWidth + gap);
  const p50X = getHistogramMarkerX(p50, buckets, barX, barWidth);
  const p90X = getHistogramMarkerX(p90, buckets, barX, barWidth);

  return (
    <Box>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height={height}
        role="img"
        aria-label="TAT distribution histogram"
      >
        {buckets.map((bucket, index) => {
          const countHeight =
            bucket.count > 0 ? (bucket.count / maxCount) * innerHeight : 0;
          const x = barX(index);
          const y = pad.top + innerHeight - countHeight;
          const isOverflow = bucket.hi == null;
          return (
            <g key={`${bucket.lo}-${bucket.hi ?? "overflow"}-${index}`}>
              <title>{`${formatBucketLabel(bucket)}: ${bucket.count}`}</title>
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={countHeight}
                rx={2}
                fill={
                  isOverflow
                    ? customColors.greyTextColor
                    : customColors.darkBlue
                }
                opacity={isOverflow ? 0.55 : 0.85}
              />
              <text
                x={x + barWidth / 2}
                y={height - 8}
                textAnchor="middle"
                fill={customColors.greyTextColor}
                fontSize={buckets.length > 10 ? 8 : 10}
              >
                {formatBucketLabel(bucket)}
              </text>
            </g>
          );
        })}
        {p50X != null ? (
          <g>
            <line
              x1={p50X}
              x2={p50X}
              y1={pad.top - 4}
              y2={pad.top + innerHeight}
              stroke={customColors.darkBlue}
              strokeWidth={1.5}
              strokeDasharray="3 3"
            />
            <text
              x={p50X + 4}
              y={pad.top - 6}
              fill={customColors.darkBlue}
              fontSize={10}
              fontWeight={700}
            >
              P50
            </text>
          </g>
        ) : null}
        {p90X != null ? (
          <g>
            <line
              x1={p90X}
              x2={p90X}
              y1={pad.top - 4}
              y2={pad.top + innerHeight}
              stroke={customColors.errorRed}
              strokeWidth={1.5}
              strokeDasharray="3 3"
            />
            <text
              x={p90X + 4}
              y={pad.top - 6}
              fill={customColors.errorRed}
              fontSize={10}
              fontWeight={700}
            >
              P90
            </text>
          </g>
        ) : null}
      </svg>
    </Box>
  );
};

const DailyTasksPill: React.FC<{
  columnKey: string;
  value: unknown;
}> = ({ columnKey, value }) => {
  const theme = useTheme();

  try {
    let label = formatUnknownText(value);
    let tone: DailyTasksChipTone = "neutral";

    if (columnKey === "status") {
      const status = String(value ?? "").toLowerCase();
      if (status === "ok" || status === "approved") tone = "ok";
      else if (status === "uploaded") tone = "info";
      else if (status === "warn" || status === "rework") tone = "warn";
      else if (status === "bad" || status === "rejected") tone = "bad";
      label = status
        ? `${status.charAt(0).toUpperCase()}${status.slice(1)}`
        : "—";
    } else if (columnKey === "path" || columnKey === "scope") {
      label =
        value === "custom" ||
        value === "stock" ||
        value === "product" ||
        value === "order"
          ? String(value)
          : label;
      tone = "info";
    } else if (columnKey === "is_open") {
      const isOpen = value === true;
      label = value == null ? "—" : isOpen ? "Open" : "Closed";
      tone = isOpen ? "info" : "neutral";
    }

    return (
      <Chip
        size="small"
        label={label}
        sx={{
          ...dailyTasksChipSx(theme, tone),
          maxWidth: "100%",
        }}
      />
    );
  } catch (error) {
    console.error(error);
    return (
      <CustomTypography
        variant="body4"
        color={theme.palette.customColors.greyTextColor}
      >
        —
      </CustomTypography>
    );
  }
};

const renderTableCell = (column: DailyTasksTatTableColumn, value: unknown) => {
  try {
    switch (column.type) {
      case "number":
        return formatNumberCell(value, column.unit);
      case "date":
      case "datetime":
        return formatUnknownText(value);
      case "pill":
        return <DailyTasksPill columnKey={column.key} value={value} />;
      case "text":
      default:
        return formatUnknownText(value);
    }
  } catch (error) {
    console.error(error);
    return "—";
  }
};

const rowKeyFor = (row: Record<string, unknown>, index: number): string => {
  try {
    const parts = [
      row.order_id,
      row.product_id,
      row.event_date,
      row.po_id,
      row.pr_id,
      row.ref,
      row.is_open,
      index,
    ];
    return parts.map((part) => String(part ?? "")).join("-");
  } catch (error) {
    console.error(error);
    return `row-${index}`;
  }
};

const DailyTasksDrilldownDrawer: React.FC<DailyTasksDrilldownDrawerProps> = ({
  store,
}) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const customColors = theme.palette.customColors;
  const [formulaOpen, setFormulaOpen] = useState(true);

  const open = store.drawerOpen;
  const drilldown = store.drilldownResult;
  const data = drilldown?.data ?? null;
  const query = store.drilldownQuery;
  const columns = data?.table.columns ?? [];
  const rows = data?.table.rows ?? [];
  const sortableColumns = getSortWhitelist(
    data?.metricId || store.drawerMetricId || "",
    columns,
  );
  const activeSort = data?.table.sort ?? query.sort;
  const page = data?.table.page ?? query.page;
  const pageSize = data?.table.page_size ?? query.page_size;
  const total = data?.table.total ?? 0;
  const visibleRange = getVisibleRowRange(page, pageSize, total, rows.length);
  const cyclesCount = data ? getCyclesCount(data.stats) : null;
  const isActivity = isDailyTasksActivityMetric(
    data?.metricId || store.drawerMetricId || "",
  );

  useEffect(() => {
    try {
      if (open) {
        setFormulaOpen(true);
      }
    } catch (error) {
      console.error(error);
    }
  }, [open, store.drawerMetricId]);

  const handleClose = () => {
    try {
      store.closeDrawer();
    } catch (error) {
      console.error(error);
    }
  };

  const handleRowClick = (row: Record<string, unknown>) => {
    try {
      const orderId = Number(row.order_id);
      const productId = Number(row.product_id);
      if (!Number.isInteger(orderId) || orderId <= 0) {
        return;
      }
      if (!Number.isInteger(productId) || productId <= 0) {
        return;
      }
      store.openProductDrawer(orderId, productId);
    } catch (error) {
      console.error(error);
    }
  };

  const handleScopeChange = async (scope: DailyTasksTatRecordScope) => {
    try {
      if (scope === query.record_scope) {
        return;
      }
      await store.fetchDrilldown({ record_scope: scope, page: 1 });
    } catch (error) {
      console.error(error);
    }
  };

  const handleSortChange = async (column: string, sortType: SortType) => {
    try {
      if (!sortableColumns.includes(column)) {
        return;
      }
      const dir: DailyTasksTatSortDir =
        sortType === SortType.ASC ? "asc" : "desc";
      await store.fetchDrilldown({
        sort: { column, dir },
        page: 1,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handlePageChange = async (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    nextPage: number,
  ) => {
    try {
      await store.fetchDrilldown({ page: nextPage });
    } catch (error) {
      console.error(error);
    }
  };

  const handleRowsPerPageChange = async (limit: number) => {
    try {
      const pageSizeNext = Math.min(Math.max(limit, 1), MAX_PAGE_SIZE);
      await store.fetchDrilldown({ page_size: pageSizeNext, page: 1 });
    } catch (error) {
      console.error(error);
    }
  };

  const handleExportCsv = async () => {
    try {
      await store.exportCsv();
    } catch (error) {
      console.error(error);
    }
  };

  const handleToggleFormula = () => {
    try {
      setFormulaOpen((current) => {
        const next = !current;
        trackDailyTasksAction("formula_toggle", store.filters, {
          expanded: next,
          metricId: store.drawerMetricId || undefined,
          cardTitle: store.drawerCardTitle || undefined,
          cardSection: store.drawerCardSection || undefined,
          context: {
            formula_expanded: next,
            metric_id: store.drawerMetricId || undefined,
            ...(store.drawerCardTitle
              ? { card_title: store.drawerCardTitle }
              : {}),
            ...(store.drawerCardSection
              ? { card_section: store.drawerCardSection }
              : {}),
          },
        });
        return next;
      });
    } catch (error) {
      console.error(error);
    }
  };

  const kicker = data?.kicker || "TAT drilldown";
  const title = data?.title || store.drawerMetricId || "Metric";
  const activityCopy = isActivity
    ? DAILY_TASKS_ACTIVITY_DRAWER_COPY[
        data?.metricId || store.drawerMetricId || ""
      ]
    : undefined;
  const desc = activityCopy?.desc || (isActivity ? "" : data?.desc || "");
  const recordNote = activityCopy?.note ?? data?.note;
  const showHistogram = Boolean(data?.distribution?.buckets?.length);
  const isPending = data != null && data.ready === false;
  const showEmptyRows =
    data != null &&
    data.ready === true &&
    rows.length === 0 &&
    !store.drilldownLoading;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 880, md: 960 },
          maxWidth: "100vw",
          height: "100%",
          maxHeight: "100dvh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
      ModalProps={{
        keepMounted: true,
        sx: {
          zIndex: (muiTheme) => muiTheme.zIndex.drawer + DRAWER_Z_OFFSET,
        },
        slotProps: {
          backdrop: {
            sx: {
              zIndex: (muiTheme) =>
                muiTheme.zIndex.drawer + DRAWER_Z_OFFSET - 1,
            },
          },
        },
      }}
    >
      {store.selectedRow ? (
        <DailyTasksDrawer
          store={store}
          embedded
          onBack={() => store.closeProductDrawer()}
        />
      ) : (
        <>
          <Box sx={modernDrawerHeaderSx(theme)}>
            <Box display="flex" gap={1.5} alignItems="flex-start" minWidth={0}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: customColors.lightBlue,
                  color: customColors.darkBlue,
                }}
              >
                <InsightsOutlinedIcon fontSize="small" />
              </Box>
              <Box minWidth={0}>
                <CustomTypography
                  variant="caption"
                  sx={{
                    color: colors.subtitle,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    fontWeight: 600,
                  }}
                >
                  {kicker}
                </CustomTypography>
                <CustomTypography
                  variant="heading5"
                  sx={{ color: colors.title, wordBreak: "break-word" }}
                >
                  {title}
                </CustomTypography>
                {desc ? (
                  <CustomTypography
                    variant="body4"
                    sx={{
                      mt: 0.5,
                      color: colors.subtitle,
                      fontSize: { xs: "0.8rem", sm: "0.875rem" },
                    }}
                  >
                    {desc}
                  </CustomTypography>
                ) : null}
              </Box>
            </Box>
            <IconButton onClick={handleClose} size="small" aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              ...modernDrawerBodySx(),
              flex: 1,
              minHeight: 0,
              overflow: "auto",
            }}
          >
            {store.drilldownError ? (
              <Alert
                severity="error"
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${colors.infoBorder}`,
                }}
              >
                {store.drilldownError}
              </Alert>
            ) : null}
            {store.exportError ? (
              <Alert
                severity="error"
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${colors.infoBorder}`,
                }}
              >
                {store.exportError}
              </Alert>
            ) : null}

            {store.drilldownLoading && !data ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: 220,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            ) : null}

            {data ? (
              <>
                <Grid2 container spacing={1.5}>
                  {data.stats.map((stat) => {
                    const clay = stat.color === "clay";
                    const tone = clay
                      ? getStatusTone(theme, "clay")
                      : {
                          fg: customColors.navyText,
                          bg: customColors.white,
                        };
                    return (
                      <Grid2 key={stat.label} size={{ xs: 6, sm: 4, md: 2 }}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 1.5,
                            height: "100%",
                            borderRadius: 2.5,
                            bgcolor: clay ? tone.bg : customColors.white,
                            border: `1px solid ${customColors.softCardBorder}`,
                            borderLeft: `4px solid ${clay ? tone.fg : customColors.darkBlue}`,
                            boxShadow: `0 2px 8px ${customColors.shadowVeryLight}`,
                          }}
                        >
                          <CustomTypography
                            variant="body4"
                            sx={{
                              color: clay
                                ? tone.fg
                                : customColors.mutedSlateText,
                              fontSize: "0.7rem",
                              fontWeight: 600,
                              letterSpacing: "0.02em",
                              textTransform: "uppercase",
                            }}
                          >
                            {stat.label}
                          </CustomTypography>
                          <CustomTypography
                            variant="heading5"
                            sx={{
                              mt: 0.5,
                              color: clay ? tone.fg : customColors.navyText,
                              fontWeight: 700,
                              fontVariantNumeric: "tabular-nums",
                              wordBreak: "break-word",
                              hyphens: "none",
                            }}
                          >
                            {formatStatValue(stat)}
                          </CustomTypography>
                        </Paper>
                      </Grid2>
                    );
                  })}
                </Grid2>

                {!isActivity ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      border: `1px solid ${colors.border}`,
                      overflow: "hidden",
                      boxShadow: `0 2px 8px ${customColors.shadowVeryLight}`,
                    }}
                  >
                    <Box
                      onClick={handleToggleFormula}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        px: 2,
                        py: 1,
                        cursor: "pointer",
                        bgcolor: customColors.white,
                        "&:hover": { bgcolor: customColors.hoverGray },
                      }}
                    >
                      <CustomTypography
                        variant="heading7"
                        sx={{ color: colors.title }}
                      >
                        How this is calculated
                      </CustomTypography>
                      <IconButton
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleToggleFormula();
                        }}
                        aria-label={
                          formulaOpen ? "Collapse formula" : "Expand formula"
                        }
                      >
                        {formulaOpen ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    </Box>
                    <Collapse in={formulaOpen} timeout="auto" unmountOnExit>
                      <Box sx={{ px: 2, pb: 2 }}>
                        <CustomTypography
                          variant="body2"
                          sx={{
                            color: customColors.navyText,
                            whiteSpace: "pre-wrap",
                          }}
                        >
                          {data.formula || "—"}
                        </CustomTypography>
                      </Box>
                    </Collapse>
                  </Paper>
                ) : null}

                {showHistogram && data.distribution ? (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      border: `1px solid ${colors.border}`,
                      bgcolor: customColors.white,
                      boxShadow: `0 2px 8px ${customColors.shadowVeryLight}`,
                    }}
                  >
                    <CustomTypography
                      variant="heading7"
                      sx={{ color: colors.title, mb: 1 }}
                    >
                      Distribution
                    </CustomTypography>
                    <DailyTasksHistogram distribution={data.distribution} />
                  </Paper>
                ) : null}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1.5,
                    flexWrap: "wrap",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    <CustomTypography
                      variant="heading7"
                      sx={{ color: colors.title }}
                    >
                      Records
                    </CustomTypography>
                    {isActivity
                      ? null
                      : SCOPE_OPTIONS.map((option) => (
                          <AppButton
                            key={option.id}
                            variant={
                              query.record_scope === option.id
                                ? "primary"
                                : "transparentPrimary"
                            }
                            label={option.label}
                            onClick={() => {
                              void handleScopeChange(option.id);
                            }}
                            disabled={store.drilldownLoading}
                            customStyles={{
                              minHeight: 32,
                              px: 1.5,
                            }}
                            fontSize="0.75rem"
                          />
                        ))}
                  </Box>
                  <AppButton
                    variant="primary"
                    label={store.exportLoading ? "Exporting..." : "Export CSV"}
                    startIcon={<DownloadIcon />}
                    onClick={() => {
                      void handleExportCsv();
                    }}
                    disabled={store.exportLoading || !store.drawerMetricId}
                    customStyles={{ minHeight: 32 }}
                    fontSize="0.75rem"
                  />
                </Box>

                <CustomTypography
                  variant="body4"
                  sx={{
                    color: customColors.mutedSlateText,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {total <= 0
                    ? "Showing 0 of 0"
                    : `Showing ${visibleRange.from}–${visibleRange.to} of ${total}`}
                  {!isActivity &&
                  cyclesCount != null &&
                  query.record_scope !== "measured"
                    ? ` · Cycles ${formatOpsTatStatValue(cyclesCount, "", "Cycles")} measured`
                    : null}
                </CustomTypography>

                {recordNote ? (
                  <CustomTypography
                    variant="body4"
                    sx={{ color: customColors.mutedSlateText }}
                  >
                    {!isActivity && query.record_scope !== "measured"
                      ? `Stats stay on the measured set. ${recordNote}`
                      : recordNote}
                  </CustomTypography>
                ) : !isActivity && query.record_scope !== "measured" ? (
                  <CustomTypography
                    variant="body4"
                    sx={{ color: customColors.mutedSlateText }}
                  >
                    Stats stay on the measured set when viewing open or all
                    rows.
                  </CustomTypography>
                ) : null}

                {isPending ? (
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="8rem"
                    textAlign="center"
                    border="1px dashed"
                    borderColor="divider"
                    borderRadius={2}
                    px={2}
                    py={2}
                    gap={1}
                  >
                    <CustomTypography
                      variant="heading6"
                      fontWeight={500}
                      color={customColors.greyTextColor}
                    >
                      This metric is pending.
                    </CustomTypography>
                    {data.note ? (
                      <CustomTypography
                        variant="body4"
                        color={customColors.greyTextColor}
                      >
                        {data.note}
                      </CustomTypography>
                    ) : null}
                  </Box>
                ) : null}

                <Paper
                  sx={{
                    ...modernTablePaperSx(theme),
                    mt: 0,
                    position: "relative",
                    overflow: "visible",
                  }}
                >
                  {store.drilldownLoading ? (
                    <Box
                      sx={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: `${customColors.white}99`,
                        zIndex: 1,
                      }}
                    >
                      <CircularProgress size={28} />
                    </Box>
                  ) : null}

                  {showEmptyRows ? (
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      height="10rem"
                      textAlign="center"
                      border="1px dashed"
                      borderColor="divider"
                      borderRadius={2}
                      m={2}
                    >
                      <CustomTypography
                        variant="heading6"
                        fontWeight={500}
                        color={customColors.greyTextColor}
                      >
                        No records for this scope.
                      </CustomTypography>
                    </Box>
                  ) : (
                    <TableContainer sx={{ overflowX: "auto" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <AppTableHeaderCell
                              txtAlign="right"
                              fontColor={colors.title}
                              fontWeight={700}
                              fontSize="0.75rem"
                              minWidth="48px"
                              sx={tableHeaderSx(theme, "right")}
                            >
                              #
                            </AppTableHeaderCell>
                            {columns.map((column) => {
                              const canSort = sortableColumns.includes(
                                column.key,
                              );
                              const align = columnAlign(column);
                              return (
                                <AppTableHeaderCell
                                  key={column.key}
                                  txtAlign={align}
                                  fontColor={colors.title}
                                  fontWeight={700}
                                  fontSize="0.75rem"
                                  minWidth={columnMinWidth(column)}
                                  sortColumn={canSort ? column.key : undefined}
                                  sortBy={
                                    canSort ? activeSort?.column : undefined
                                  }
                                  sortDirection={
                                    canSort && activeSort
                                      ? activeSort.dir === "asc"
                                        ? SortType.ASC
                                        : SortType.DESC
                                      : undefined
                                  }
                                  onSortChange={
                                    canSort ? handleSortChange : undefined
                                  }
                                  sx={tableHeaderSx(theme, align)}
                                >
                                  {column.label}
                                </AppTableHeaderCell>
                              );
                            })}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.map((row, index) => (
                            <TableRow
                              key={rowKeyFor(row, index)}
                              hover
                              onClick={() => handleRowClick(row)}
                              sx={{
                                ...modernTableRowSx(theme),
                                cursor:
                                  Number(row.product_id) > 0
                                    ? "pointer"
                                    : "default",
                              }}
                            >
                              <AppTableRowCell
                                textAlign="right"
                                align="right"
                                fontWeight={600}
                                fontSize="0.75rem"
                                color={customColors.mutedSlateText}
                                sx={{
                                  ...tableBodySx(theme, "right"),
                                  fontVariantNumeric: "tabular-nums",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {getSerialNumber(page, pageSize, index)}
                              </AppTableRowCell>
                              {columns.map((column) => {
                                const align = columnAlign(column);
                                return (
                                  <AppTableRowCell
                                    key={`${column.key}-${index}`}
                                    textAlign={align}
                                    align={align}
                                    fontWeight={500}
                                    fontSize="0.75rem"
                                    color={customColors.navyText}
                                    sx={{
                                      ...tableBodySx(theme, align),
                                      fontVariantNumeric:
                                        column.type === "number"
                                          ? "tabular-nums"
                                          : undefined,
                                      whiteSpace:
                                        column.type === "date" ||
                                        column.type === "datetime" ||
                                        column.type === "pill"
                                          ? "nowrap"
                                          : undefined,
                                    }}
                                  >
                                    {renderTableCell(column, row[column.key])}
                                  </AppTableRowCell>
                                );
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Paper>
              </>
            ) : null}
          </Box>

          {data && columns.length > 0 && !isPending ? (
            <Box
              sx={{
                flexShrink: 0,
                borderTop: `1px solid ${colors.border}`,
                bgcolor: customColors.white,
                px: { xs: 0.5, sm: 1 },
              }}
            >
              <AppTablePagenation
                fontSize="12px"
                pageNo={page}
                limit={pageSize}
                totalCount={total}
                onPageChange={handlePageChange}
                handleRowsChange={handleRowsPerPageChange}
                rowsPerPageOptions={PAGE_SIZE_OPTIONS}
              />
            </Box>
          ) : null}
        </>
      )}
    </Drawer>
  );
};

export default observer(DailyTasksDrilldownDrawer);
